import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

const ExamResultModal = ({
  isOpen,
  onClose,
  submitResult,
  examName = 'Biology Unit Test',
  className = 'A/L Bio Class',
  onCheckPaper
}) => {
  // Use backend grading data
  const summary = submitResult?.gradingSummary || {};
  const detailedResults = submitResult?.detailedResults || [];

  const totalCount = summary.totalQuestions || 0;
  const correctCount = summary.correctCount || 0;
  const incorrectCount = summary.incorrectCount || 0;
  const scorePercentage = summary.percentage || 0;
  const totalMarksAwarded = summary.totalMarksAwarded || 0;
  const totalMarksPossible = summary.totalMarksPossible || 0;
  const grade = summary.grade || '-';

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      disableScrollLock
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)'
        },
        '& .MuiPaper-root': {
          borderRadius: '24px !important',
          width: '100%',
          maxWidth: '560px',
          margin: '16px',
          boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          fontFamily: "'Poppins', sans-serif",
          overflow: 'hidden',
          position: 'relative',
          p: { xs: 3, sm: 4 },
          pt: { xs: 4, sm: 5 }
        }
      }}
    >
      {/* Top Right Close Button */}
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          color: '#94a3b8',
          transition: 'all 0.25s',
          '&:hover': {
            color: '#64748b',
            backgroundColor: '#f1f5f9'
          }
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 750,
                color: '#1e293b',
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.3
              }}
            >
              {examName}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: '#94a3b8',
                fontFamily: "'Poppins', sans-serif",
                mt: 0.5,
                fontWeight: 500
              }}
            >
              {className}
            </Typography>
          </Box>

          {/* Performance stats row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1.5,
              width: '100%'
            }}
          >
            {[
              { value: totalCount, label: 'Total', bgcolor: '#f1f5f9', color: '#475569' },
              { value: correctCount, label: 'Correct', bgcolor: '#d1fae5', color: '#059669' },
              { value: incorrectCount, label: 'Incorrect', bgcolor: '#fee2e2', color: '#dc2626' },
              { value: `${totalMarksAwarded}/${totalMarksPossible}`, label: 'Marks', bgcolor: '#ede9fe', color: '#7c3aed' }
            ].map((stat, idx) => (
              <Box key={idx} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    bgcolor: stat.bgcolor,
                    color: stat.color,
                    borderRadius: '12px',
                    py: 1,
                    fontSize: '16px',
                    fontWeight: 750,
                    fontFamily: 'Poppins',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 0.5
                  }}
                >
                  {stat.value}
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, fontFamily: 'Poppins' }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Score and Grade row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontWeight: 750,
                fontSize: '17px',
                color: '#1e293b',
                fontFamily: 'Poppins',
                lineHeight: 1
              }}
            >
              Score : {scorePercentage}%
            </Typography>
            <Box
              sx={{
                bgcolor: scorePercentage >= 50 ? '#d1fae5' : '#fee2e2',
                color: scorePercentage >= 50 ? '#059669' : '#dc2626',
                borderRadius: '8px',
                px: 2,
                py: 0.75,
                fontSize: '14px',
                fontWeight: 750,
                fontFamily: 'Poppins'
              }}
            >
              Grade: {grade}
            </Box>
          </Box>

          {/* Scrollable container for color-coded results navigator map */}
          <Box
            sx={{
              maxHeight: '190px',
              overflowY: 'auto',
              width: '100%',
              pr: 0.5,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#cbd5e1',
                borderRadius: '3px'
              }
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 1fr)',
                gap: 0.75,
                width: '100%'
              }}
            >
              {detailedResults
                .sort((a, b) => a.questionOrder - b.questionOrder)
                .map((result) => {
                  let cellBg = '#f1f5f9';
                  let cellColor = '#94a3b8';
                  let cellBorder = '1px solid #cbd5e1';

                  if (result.studentAnswer != null && result.studentAnswer !== '') {
                    if (result.isCorrect) {
                      cellBg = '#d1fae5';
                      cellColor = '#059669';
                      cellBorder = '1px solid #059669';
                    } else {
                      cellBg = '#fee2e2';
                      cellColor = '#dc2626';
                      cellBorder = '1px solid #dc2626';
                    }
                  }

                  return (
                    <Box
                      key={result.questionId}
                      sx={{
                        aspectRatio: '1',
                        borderRadius: '6px',
                        border: cellBorder,
                        bgcolor: cellBg,
                        color: cellColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 700,
                        fontFamily: 'Poppins'
                      }}
                    >
                      {result.questionOrder.toString().padStart(2, '0')}
                    </Box>
                  );
                })}
            </Box>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" spacing={2} sx={{ width: '100%', mt: 1 }}>
            <Button
              onClick={onCheckPaper}
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: '9999px',
                py: 1.5,
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                borderColor: '#FF6244',
                color: '#FF6244',
                borderWidth: '1.5px',
                '&:hover': {
                  borderColor: '#ff4f2b',
                  bgcolor: 'rgba(255, 98, 68, 0.05)',
                  borderWidth: '1.5px'
                }
              }}
            >
              Check paper
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                flex: 1,
                background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                color: '#ffffff',
                borderRadius: '9999px',
                py: 1.5,
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                '&:hover': {
                  background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                transition: 'all 0.25s ease'
              }}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExamResultModal;
